import React, { useEffect, useState } from 'react';
import { DefaultButton, MessageBar, MessageBarType, Spinner } from '@fluentui/react';
import { getMaluchEntitySource, confirmMaluchEmpatia, rejectMaluchCandidate } from '../api/prospectingApi';
import MaluchOperatorFacilities from './MaluchOperatorFacilities';

/** Source card inside the *existing* entity panel; no separate Maluch section. */
export default function MaluchEntitySource({ token, entityId, onChanged, onOpenEntity }) {
  const [entries, setEntries] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [candidate, setCandidate] = useState({});
  useEffect(() => {
    if (!entityId || !token) return undefined;
    const controller = new AbortController();
    setBusy(true); setError('');
    getMaluchEntitySource(token, entityId, controller.signal)
      .then((payload) => setEntries(payload.items || []))
      .catch((e) => { if (e.name !== 'AbortError') setError(e.message || 'Błąd źródła MALUCH+.'); })
      .finally(() => setBusy(false));
    return () => controller.abort();
  }, [token, entityId]);
  async function confirm(a) {
    const entity = candidate[a.id] || a.candidate_entity_id;
    if (!entity) return;
    if (!window.confirm(`Potwierdzić, że wniosek ${a.application_no} dotyczy dokładnie placówki Empatia ${entity}? Nie sugeruj się samą gminą ani zbliżoną pojemnością.`)) return;
    setBusy(true); setError('');
    try {
      await confirmMaluchEmpatia(token, a.id, entity);
      onChanged?.();
      setEntries((old) => old.map((entry) => entry.id === a.id ? { ...entry, match_status: 'confirmed', matched_entity_id: entity, provisional_entity_id: null } : entry));
    } catch (e) { setError(e.message || 'Nie udało się potwierdzić powiązania.'); }
    finally { setBusy(false); }
  }
  async function reject(a) {
    if (!window.confirm('Odrzucić dopasowanie do Empatii? ONE odtworzy oddzielną pozycję MALUCH+ na mapie, co zwiększy potencjalny ARR.')) return;
    setBusy(true);setError('');
    try {
      await rejectMaluchCandidate(token,a.id);
      setEntries((old) => old.filter((entry) => entry.id !== a.id));
      onChanged?.();
    } catch(e) {setError(e.message || 'Nie udało się odrzucić kandydata.');}
    finally {setBusy(false);}
  }
  if (!entries.length && !busy && !error) return null;
  return (
    <section className="prospecting-maluch-source">
      <h3>MALUCH+ / Aktywny Maluch — źródło inwestycji</h3>
      {busy ? <Spinner label="Pobieram źródło MALUCH+…" /> : null}
      {error ? <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar> : null}
      {entries.map((a) => (
        <div key={a.id} className="prospecting-detail-note">
          <strong>{a.application_no} · {a.care_type === 'nursery' ? 'żłobek' : a.care_type === 'club' ? 'klub dziecięcy' : 'dzienny opiekun'}</strong>
          <div>Gmina: {a.commune}, TERYT {a.teryt} · przyznane miejsca: <strong>{a.awarded_places}</strong></div>
          <div>KPO: {a.funding_kpo_places} · FERS: {a.funding_fers_places} · decyzja: {a.ministry_status}</div>
          <div>Wnioskodawca: {a.applicant} · kontakt do wniosku: {a.contact_raw || 'brak'}</div>
          <MaluchOperatorFacilities token={token} application={a} onChanged={onChanged} onOpenEntity={onOpenEntity} />
          <div>Etap: {a.match_status === 'confirmed' ? 'potwierdzono w Empatii' : a.match_status === 'linked_probable' ? 'powiązano prawdopodobnie z Empatią (bez nadpisywania danych)' : a.verified_stage && a.verified_stage !== 'unknown' ? a.verified_stage : 'etap inwestycji niezweryfikowany'}</div>
          {a.planned_opening_date ? <div>Planowane otwarcie: <strong>{a.planned_opening_date}</strong></div> : null}
          {a.actual_opening_date ? <div>Potwierdzone otwarcie: {a.actual_opening_date}</div> : null}
          {a.contact_priority === 'contact_now' ? <div><strong>Kontakt teraz — potwierdzony sygnał przedotwarciowy</strong></div> : null}
          {a.evidence_url && /^https:\/\//i.test(a.evidence_url) ? <div><a href={a.evidence_url} target="_blank" rel="noopener noreferrer">Dowód etapu inwestycji ↗</a></div> : null}
          {['confirmed','linked_probable'].includes(a.match_status) ? <div>Empatia: {a.matched_entity_id} {a.match_status === 'linked_probable' ? '· powiązanie automatyczne, wymagające ewentualnej korekty' : ''}</div> : (
            <>
              {a.candidate_entity_id ? <div>Możliwy odpowiednik w Empatii (niepotwierdzony): {a.candidate_entity_id}</div> : null}
              <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center' }}>
                <input aria-label="ID encji Empatia do potwierdzenia" placeholder="ID placówki Empatia" value={candidate[a.id] ?? a.candidate_entity_id ?? ''} onChange={(e) => setCandidate((old) => ({ ...old, [a.id]: e.target.value }))} />
                <DefaultButton text="Potwierdź Empatię" disabled={busy || !(candidate[a.id] || a.candidate_entity_id)} onClick={() => confirm(a)} />
                {a.match_status === 'candidate' ? <DefaultButton text="To inna placówka" disabled={busy} onClick={() => reject(a)} /> : null}
              </div>
            </>
          )}
        </div>
      ))}
    </section>
  );
}
